import { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Building, Home, ChevronRight, Loader2, MapPin, Eye } from 'lucide-react';
import { LeadCaptureForm } from '@/components/forms/LeadCaptureForm';
import { Header } from '@/components/layout/Header';
import { TownNavBar } from '@/components/layout/TownNavBar';
import { AreaNavBar } from '@/components/layout/AreaNavBar';
import { Footer } from '@/components/layout/Footer';
import { RealtorCard } from '@/components/realtor/RealtorCard';
import { StructuredData } from '@/components/seo/StructuredData';
import { RecentBlogPostsCard, RecentBlogPost } from '@/components/blog/RecentBlogPostsCard';
import { ImageGalleryCarousel, GalleryImage } from '@/components/gallery/ImageGalleryCarousel';

import { sanitizeHtml } from '@/lib/sanitize';
import { PublicThemeProvider } from '@/components/theme/ThemeProvider';
import { gaEvents } from '@/hooks/useGoogleAnalytics';
import { useLoanOfficerAdForArea } from '@/hooks/useLoanOfficerAd';

interface AreaData {
  id: string;
  name: string;
  slug: string;
  intro_summary: string | null;
  about_area: string | null;
  hero_image_url: string | null;
  hero_image_attribution: string | null;
  gallery_images: GalleryImage[] | null;
  highlights_json: any;
  faqs_json: any;
  zip_codes: string[] | null;
  site_id: string;
  town_id: string;
}

interface TownData {
  id: string;
  name: string;
  slug: string;
  state: string | null;
}

interface ListingData {
  id: string;
  address: string;
  city: string;
  state: string;
  price: number | null;
  beds: number | null;
  baths: number | null;
  sqft: number | null;
  image_url: string | null;
}

interface SiteData {
  id: string;
  name: string;
  favicon_url: string | null;
  theme: string;
  header_font: string | null;
  enable_market_value_report?: boolean;
  enable_community_guide?: boolean;
  enable_mortgage_info?: boolean;
  bing_auth_code?: string | null;
  realtor_assigned?: boolean;
  seller_ad_image_url?: string | null;
  mortgage_ad_image_url?: string | null;
}

interface OtherArea {
  id: string;
  name: string;
  slug: string;
}

interface AllArea {
  id: string;
  name: string;
  slug: string;
}

interface HeaderTown {
  id: string;
  name: string;
  slug: string;
}

// Public-safe realtor data (from public_realtor_profiles view)
interface RealtorData {
  id: string;
  first_name: string | null;
  last_name: string | null;
  photo_url: string | null;
  logo_url: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  license_numbers: string | null;
  about_me: string | null;
  title_preference?: string | null;
  ad_image_url?: string | null;
}

export default function AreaPage() {
  const { townSlug, areaSlug } = useParams<{ townSlug: string; areaSlug: string }>();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === 'true';
  
  const [area, setArea] = useState<AreaData | null>(null);
  const [town, setTown] = useState<TownData | null>(null);
  const [site, setSite] = useState<SiteData | null>(null);
  const [realtor, setRealtor] = useState<RealtorData | null>(null);
  const [listings, setListings] = useState<ListingData[]>([]);
  const [otherAreas, setOtherAreas] = useState<OtherArea[]>([]);
  const [allAreas, setAllAreas] = useState<AllArea[]>([]);
  const [headerTowns, setHeaderTowns] = useState<HeaderTown[]>([]);
  const [recentBlogPosts, setRecentBlogPosts] = useState<RecentBlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Loan officer ad - prioritizes loan officer ad over site mortgage ad
  const { data: loanOfficerAd } = useLoanOfficerAdForArea(area?.id || null, town?.id || null);
  const mortgageAdImageUrl = loanOfficerAd?.ad_image_url || site?.mortgage_ad_image_url;
  const mortgageAdAlt = loanOfficerAd 
    ? `${loanOfficerAd.first_name} ${loanOfficerAd.last_name} - NMLS: ${loanOfficerAd.license_number || 'N/A'} - Mortgage Services`
    : 'Mortgage Information and Tips';

  useEffect(() => {
    if (townSlug && areaSlug) {
      fetchAreaData();
    }
  }, [townSlug, areaSlug, isPreview]);

  const fetchAreaData = async () => {
    setLoading(true);
    try {
      // First, resolve the site by hostname for multi-tenancy
      const hostname = window.location.hostname;
      const isDevelopment = 
        hostname === 'localhost' || 
        hostname.includes('lovable.app') ||
        hostname.includes('127.0.0.1');

      let siteId: string | null = null;

      if (!isDevelopment) {
        // Production: Try to match by primary_domain
        const hostWithoutWww = hostname.replace(/^www\./, '');
        const hostWithWww = hostname.startsWith('www.') ? hostname : `www.${hostname}`;

        const { data: domainMatch } = await supabase
          .from('sites_public' as any)
          .select('id')
          .eq('status', 'active')
          .or(`primary_domain.eq.${hostname},primary_domain.eq.${hostWithoutWww},primary_domain.eq.${hostWithWww}`)
          .limit(1)
          .maybeSingle();

        if (domainMatch) {
          siteId = (domainMatch as any).id;
          console.log('Area page: Site resolved by domain:', hostname, '->', siteId);
        }
      }

      // Fallback to first active site for development (use oldest site)
      if (!siteId) {
        const { data: fallbackSite } = await supabase
          .from('sites_public' as any)
          .select('id')
          .eq('status', 'active')
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();
        
        siteId = (fallbackSite as any)?.id || null;
      }

      if (!siteId) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      // First fetch the town by slug AND site_id for multi-tenancy
      let townQuery = supabase
        .from('towns')
        .select('id, name, slug, state')
        .eq('slug', townSlug)
        .eq('site_id', siteId);
      
      if (!isPreview) {
        townQuery = townQuery.eq('is_published', true);
      }
      
      const { data: townData, error: townError } = await townQuery.single();

      if (townError || !townData) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setTown(townData);

      // Fetch area by slug within this town - allow unpublished in preview mode
      let areaQuery = supabase
        .from('areas')
        .select('*')
        .eq('slug', areaSlug)
        .eq('town_id', townData.id);
      
      if (!isPreview) {
        areaQuery = areaQuery.eq('is_published', true);
      }
      
      const { data: areaData, error: areaError } = await areaQuery.single();

      if (areaError || !areaData) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      // Cast JSONB fields to proper types
      setArea({
        ...areaData,
        gallery_images: Array.isArray(areaData.gallery_images) ? (areaData.gallery_images as unknown as GalleryImage[]) : null,
      } as AreaData);

      // Fetch site, listings, other areas, all areas for nav, and header towns in parallel
      let otherAreasQuery = supabase.from('areas').select('id, name, slug').eq('town_id', townData.id).neq('id', areaData.id).limit(6);
      if (!isPreview) {
        otherAreasQuery = otherAreasQuery.eq('is_published', true);
      }

      // All areas query for the nav bar
      let allAreasQuery = supabase.from('areas').select('id, name, slug').eq('town_id', townData.id).order('name');
      if (!isPreview) {
        allAreasQuery = allAreasQuery.eq('is_published', true);
      }

      // Header towns query - all towns in the site
      let headerTownsQuery = supabase
        .from('towns')
        .select('id, name, slug')
        .eq('site_id', areaData.site_id)
        .order('name');
      if (!isPreview) {
        headerTownsQuery = headerTownsQuery.eq('is_published', true);
      }
      
      const [siteRes, listingsRes, otherAreasRes, allAreasRes, realtorRes, headerTownsRes, blogPostsRes] = await Promise.all([
        supabase.from('sites_public' as any).select('id, name, favicon_url, theme, header_font, enable_market_value_report, enable_community_guide, enable_mortgage_info, bing_auth_code, realtor_assigned, seller_ad_image_url, mortgage_ad_image_url').eq('id', areaData.site_id).maybeSingle(),
        supabase.from('listings').select('id, address, city, state, price, beds, baths, sqft, image_url').eq('area_id', areaData.id).eq('status', 'active').limit(12),
        otherAreasQuery,
        allAreasQuery,
        supabase.from('realtor_profiles').select('id, first_name, last_name, photo_url, logo_url, phone, city, state, license_numbers, about_me, title_preference, ad_image_url').eq('site_id', areaData.site_id).single(),
        headerTownsQuery,
        supabase
          .from('blog_posts')
          .select('id, title, slug, category, main_image_url')
          .eq('site_id', areaData.site_id)
          .eq('is_published', true)
          .order('published_at', { ascending: false })
          .limit(3),
      ]);

      if (siteRes.data) setSite(siteRes.data as unknown as SiteData);
      if (listingsRes.data) setListings(listingsRes.data);
      if (otherAreasRes.data) setOtherAreas(otherAreasRes.data);
      if (allAreasRes.data) setAllAreas(allAreasRes.data);
      if (realtorRes.data) setRealtor(realtorRes.data);
      if (headerTownsRes.data) setHeaderTowns(headerTownsRes.data);
      if (blogPostsRes.data) setRecentBlogPosts(blogPostsRes.data);

      // Track area view event
      gaEvents.viewArea(areaData.id, areaData.name);
    } catch (error) {
      console.error('Error fetching area:', error);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Loading...</title>
          <meta name="description" content="" />
          <meta property="og:title" content="" />
          <meta property="og:description" content="" />
          <meta name="twitter:title" content="" />
          <meta name="twitter:description" content="" />
        </Helmet>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </>
    );
  }

  if (notFound || !area || !town) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header siteName="Real Estate" />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-display font-bold mb-4">Area Not Found</h1>
            <p className="text-muted-foreground mb-6">The area you're looking for doesn't exist or isn't published yet.</p>
            <Button asChild>
              <Link to="/">Return Home</Link>
            </Button>
          </div>
        </main>
        <Footer siteName="Real Estate" />
      </div>
    );
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price);
  };

  return (
    <PublicThemeProvider themeId={site?.theme || 'classic'} headerFontId={site?.header_font || 'playfair-display'}>
      <Helmet>
        <title>{area.name}, {town.name} {town.state || 'MA'} Real Estate | {site?.name || 'Real Estate'}</title>
        <meta name="description" content={area.intro_summary || `Explore real estate in ${area.name}, ${town.name} ${town.state || 'MA'}`} />
        {site?.bing_auth_code && <meta name="msvalidate.01" content={site.bing_auth_code} />}
        {/* Canonical URL is injected by Cloudflare worker using site's canonical_base_url */}
        <meta name="robots" content="index, follow" />
        
        {/* Preload LCP hero image with high priority to reduce resource load delay */}
        {area.hero_image_url && <link rel="preload" as="image" href={area.hero_image_url} fetchPriority="high" />}
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={site?.name || 'Real Estate'} />
        <meta property="og:title" content={`${area.name}, ${town.name} ${town.state || 'MA'} Real Estate | ${site?.name || 'Real Estate'}`} />
        <meta property="og:description" content={area.intro_summary || `Explore real estate in ${area.name}, ${town.name} ${town.state || 'MA'}`} />
        {/* og:url and og:image are injected by Cloudflare worker */}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${area.name}, ${town.name} ${town.state || 'MA'} Real Estate | ${site?.name || 'Real Estate'}`} />
        <meta name="twitter:description" content={area.intro_summary || `Explore real estate in ${area.name}, ${town.name} ${town.state || 'MA'}`} />
      </Helmet>
      <div className="min-h-screen flex flex-col">
      <StructuredData
        realtor={realtor}
        siteName={site?.name || 'Real Estate'}
        area={{ name: area.name, state: town.state, zip_codes: area.zip_codes, townName: town.name }}
        pageUrl={typeof window !== 'undefined' ? window.location.href : ''}
      />
      <Header
        siteName={site?.name || 'Real Estate'} 
        logoUrl={realtor?.logo_url} 
        faviconUrl={site?.favicon_url}
        enableMarketValueReport={site?.enable_market_value_report !== false}
        enableCommunityGuide={site?.enable_community_guide !== false}
        enableMortgageInfo={site?.enable_mortgage_info !== false}
      />
      
      {/* Town Navigation Bar */}
      <TownNavBar 
        towns={headerTowns} 
        currentTownSlug={town.slug} 
        isPreview={isPreview}
        enableMarketValueReport={site?.enable_market_value_report !== false}
        enableMortgageInfo={site?.enable_mortgage_info !== false}
        realtorFirstName={realtor?.first_name}
        themeId={site?.theme}
      />
      
      {/* Area Navigation Bar */}
      <AreaNavBar areas={allAreas} townSlug={town.slug} currentAreaSlug={area.slug} isPreview={isPreview} />
      
      {/* Preview Mode Banner */}
      {isPreview && (
        <div className="bg-amber-500 text-amber-950 py-2 px-4 text-center text-sm font-medium flex items-center justify-center gap-2">
          <Eye className="h-4 w-4" />
          Preview Mode — This page is not published yet
        </div>
      )}
      
      <main className="flex-1">
        {/* Hero Section */}
        <section 
          className="relative bg-gradient-hero text-white py-6 md:py-10"
          style={area.hero_image_url ? {
            backgroundImage: `linear-gradient(to bottom, rgba(30, 41, 59, 0.85), rgba(30, 41, 59, 0.95)), url(${area.hero_image_url})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          } : undefined}
        >
          <div className="container px-4 sm:px-6 lg:px-8">
            <nav className="flex items-center gap-2 text-sm mb-6 opacity-80 flex-wrap">
              <Link to="/" className="hover:underline">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <Link to="/towns" className="hover:underline">Towns</Link>
              <ChevronRight className="h-4 w-4" />
              <Link to={`/${town.slug}`} className="hover:underline">{town.name}</Link>
              <ChevronRight className="h-4 w-4" />
              <span>{area.name}</span>
            </nav>
            <h1 className="text-2xl md:text-3xl font-display font-bold mb-2">
              {area.name} Real Estate in {town.name}, {town.state || 'MA'}
            </h1>
            <h2 className="text-2xl md:text-3xl font-display font-medium mb-4 opacity-90">
              Real Estate Services in {area.name}, {town.state === 'MA' ? 'Massachusetts' : town.state || 'Massachusetts'}
            </h2>
            <p className="text-xl opacity-80">
              {area.intro_summary || `Explore homes and properties in the ${area.name} neighborhood.`}
            </p>
          </div>
        </section>

        {/* About Section with Realtor Card */}
        <section className="py-6 md:py-8">
          <div className="container px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-10 gap-8">
              {/* Left Column - About Content */}
              <div className="lg:col-span-7">
                {/* Gallery Carousel or Single Image */}
                {(() => {
                  // Build gallery from gallery_images or fall back to hero_image_url
                  const gallery: GalleryImage[] = area.gallery_images && area.gallery_images.length > 0
                    ? area.gallery_images
                    : area.hero_image_url
                      ? [{ url: area.hero_image_url, attribution: area.hero_image_attribution || undefined }]
                      : [];
                  
                  if (gallery.length > 0) {
                    return (
                      <ImageGalleryCarousel 
                        images={gallery} 
                        altPrefix={`${area.name}, ${town.name}`}
                        isHero={true}
                      />
                    );
                  }
                  return null;
                })()}
                {area.about_area && (
                  <>
                    <h2 className="text-3xl font-display font-bold mb-6">About {area.name}</h2>
                    <div 
                      className="prose prose-lg max-w-none text-muted-foreground [&>p]:mb-4 [&>p:last-child]:mb-0"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(area.about_area) }}
                    />
                  </>
                )}

                {/* Market Value Report Ad - Below About section */}
                {site?.enable_market_value_report !== false && site?.seller_ad_image_url && (
                  <div className="mt-6 max-w-md">
                    <Link to="/market-value-report" className="rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow block">
                      <img 
                        src={site.seller_ad_image_url} 
                        alt="Free Local Market Value Report for Home Sellers" 
                        className="w-full h-auto object-cover"
                      />
                    </Link>
                  </div>
                )}

                {/* Mortgage Info Ad */}
                {site?.enable_mortgage_info !== false && mortgageAdImageUrl && (
                  <div className="mt-6 max-w-md">
                    <Link to="/mortgages" className="rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow block">
                      <img 
                        src={mortgageAdImageUrl} 
                        alt={mortgageAdAlt} 
                        className="w-full h-auto object-cover"
                      />
                    </Link>
                  </div>
                )}
              </div>
              
              {/* Right Column - Latest Articles + Realtor Card & Lead Form */}
              <div className="lg:col-span-3 space-y-6">
                <RecentBlogPostsCard posts={recentBlogPosts} />
                
                {site?.realtor_assigned !== false && realtor && <RealtorCard realtor={realtor} />}
                <Card className="p-6">
                  <h3 className="text-xl font-display font-bold mb-2 text-center">
                    Interested in {area.name}?
                  </h3>
                  <p className="text-muted-foreground text-center mb-4 text-sm">
                    Get in touch and we'll help you find your perfect home.
                  </p>
                  <LeadCaptureForm 
                    siteId={area.site_id} 
                    townId={town.id} 
                    areaId={area.id}
                    pagePath={`/${town.slug}/${area.slug}`}
                    siteDomain={window.location.hostname}
                    townName={town.name}
                    areaName={area.name}
                  />
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Listings Section - Hidden until active listings are available */}

        {/* Other Areas in Town */}
        {otherAreas.length > 0 && (
          <section className="pt-6 pb-12 md:pt-8 md:pb-16">
            <div className="container px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl font-display font-bold mb-8">
                Other Neighborhoods in {town.name}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {otherAreas.map((otherArea) => (
                  <Link key={otherArea.id} to={`/${town.slug}/${otherArea.slug}${isPreview ? '?preview=true' : ''}`}>
                    <Card className="p-4 text-center hover:shadow-md transition-shadow h-full">
                      <Building className="h-6 w-6 mx-auto mb-2 text-primary" />
                      <p className="font-medium text-sm">{otherArea.name}</p>
                    </Card>
                  </Link>
                ))}
              </div>
              <div className="mt-6 text-center">
                <Button variant="outline" asChild>
                  <Link to={`/${town.slug}`}>
                    <MapPin className="h-4 w-4 mr-2" />
                    Back to {town.name}
                  </Link>
                </Button>
              </div>

            </div>
          </section>
        )}

      </main>

      <Footer 
        siteName={site?.name || 'Real Estate'} 
        towns={headerTowns} 
        areas={allAreas} 
        realtor={site?.realtor_assigned !== false ? realtor : null}
        enableBuyers={true}
        enableSellers={true}
        enableMortgageInfo={site?.enable_mortgage_info !== false}
      />
    </div>
    </PublicThemeProvider>
  );
}
